import React, { useState } from 'react';
import t from 'react-translate';
import { isEmpty, omit, pick } from 'underscore';
import { useSelector } from 'react-redux';
import uuid from 'react-uuid';

import { QuizAIQuestion } from 'redux/schemas/models/quiz';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { NovoAIItemType } from 'redux/schemas/models/lecture-page';
import { getActivity } from 'redux/selectors/timeline';
import { ActivityType } from 'redux/schemas/models/activity';
import { useAppDispatch } from 'redux/store';
import {
  patchSuggestedComponentSet,
  setAddingSuggestedComponentSetId,
  setDestinationLecturePage,
  setNovoAIProgressiveQuizQuestionModalId,
  setNovoAIShowPreviewModal,
} from 'redux/actions/lecture-pages';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

import NvModal, { ModalType } from 'shared/components/nv-modal';
import QuizAiQuestionsModal from 'lecture_pages/components/quiz/ai-questions-modal';
import { QuizTypeSelectionModalContent } from 'quizzes/components/quiz-basic-setting-modal';
import { LecturePageMode } from 'lecture_pages/components';
import GenerateButton from '../shared/generate-button';
import useAIComponentsGenerator from '../../hooks/use-ai-components-generator';
import AiModal from '../shared/ai-modal';

const GenerateQuiz = () => {
  const [showQuestionModal, setShowQuestionModal] = useState<boolean>(false);
  const [showQuizTypeModal, setShowQuizTypeModal] = useState<boolean>(false);
  const [questions, setQuestions] = useState<QuizAIQuestion[]>([]);

  const { createAIComponent } = useAIComponentsGenerator();
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();

  const novoAIProperties = useSelector(state => state.app.lecturePage.novoAI);
  const { lectureComponentToReplace, itemTrueType } = useSelector(state => state.app.lecturePage.novoAI);
  const quizActivity = useSelector((state) => getActivity(state, novoAIProperties?.lectureComponentToReplace?.quiz, ActivityType.QUIZ));
  const progressiveQuizActivity = useSelector((state) => getActivity(state, novoAIProperties?.lectureComponentToReplace?.progressiveQuiz, ActivityType.PROGRESSIVE_QUIZ));

  const isEditing = !isEmpty(lectureComponentToReplace);

  const closeQuestionModal = () => {
    setShowQuestionModal(false);
  };

  const onSelectQuestions = (AiQuestions: QuizAIQuestion[]) => {
    closeQuestionModal();

    if (isEditing) {
      const editQuiz = itemTrueType === ComponentType.PROGRESSIVE_QUIZ
        ? onSubmitSingleQuestionViewQuiz
        : onSubmitAllQuestionViewQuiz;

      editQuiz(AiQuestions);
    } else {
      setQuestions(AiQuestions);
      setShowQuizTypeModal(true);
    }
  };

  /**
   * When creating a quiz through the single-suggestion workflow, we add the
   * quiz to the Suggestions Redux state and continue the creation flow using
   * the existing lesson preview and save functionality. For editing, we continue
   * to use the existing direct component update flow.
   */
  const configureQuizSuggestionFlow = (component: LectureComponent) => {
    const componentSetId = uuid();
    dispatch(patchSuggestedComponentSet({
      id: componentSetId,
      secondaryActivities: [],
      activity: component,
    }));

    dispatch(setDestinationLecturePage({
      mode: LecturePageMode.VIEW,
      lecturePageId: params.lecturePageId,
      catalogId: params.catalogId,
    }));
    setShowQuizTypeModal(false);
    dispatch(setAddingSuggestedComponentSetId(componentSetId));
    dispatch(setNovoAIShowPreviewModal(true));
  };

  const onSubmitAllQuestionViewQuiz = (selectedQuestions: QuizAIQuestion[]) => {
    let quizData = {
      // When creating with AI, we are keeping the default type as practice
      // with unlimited quiz attempts.
      graded: false,

      // Removing the FE generated id from the payload.
      questionsAttributes: selectedQuestions.map((que) => omit(que, 'id')),
    };

    if (isEditing) {
      quizData = {
        ...quizData,
        ...pick(quizActivity, [
          'id', 'title', 'expirationDate', 'hardDeadline', 'maximumAttempts',
          'graded', 'isTodo',
        ]),
      };
    }

    if (!isEditing && novoAIProperties.itemType === NovoAIItemType.QUIZ) {
      configureQuizSuggestionFlow({
        trueType: ComponentType.QUIZ,
        type: ComponentType.QUIZ,
        quiz: quizData,
      } as LectureComponent<ComponentType.QUIZ>);
    } else {
      createAIComponent({
        itemTrueType: ComponentType.QUIZ,
        modelData: {
          quiz: quizData,
        },
      });
    }
  };

  const onSubmitSingleQuestionViewQuiz = (selectedQuestions: QuizAIQuestion[]) => {
    let quizData = {
      // AI will create quiz with Unlimited quiz attempts and 3 question attempts.
      graded: true,
      maximumAttempts: -1,
      questionMaximumAttempts: 3,

      // Removing the FE generated id from the payload.
      questionsAttributes: selectedQuestions.map((que) => omit(que, 'id')),
    };

    if (isEditing) {
      quizData = {
        ...quizData,
        ...pick(progressiveQuizActivity, [
          'id', 'isTodo', 'hardDeadline', 'expirationDate',
          'maximumAttempts', 'questionMaximumAttempts',
        ]),
      };
    }

    if (!isEditing && novoAIProperties.itemType === NovoAIItemType.QUIZ) {
      configureQuizSuggestionFlow({
        trueType: ComponentType.PROGRESSIVE_QUIZ,
        type: ComponentType.PROGRESSIVE_QUIZ,
        progressiveQuiz: quizData,
      } as LectureComponent<ComponentType.PROGRESSIVE_QUIZ>);
    } else {
      createAIComponent({
        itemTrueType: ComponentType.PROGRESSIVE_QUIZ,
        modelData: {
          progressiveQuiz: quizData,
        },
      }).then((response) => {
        if (isEditing) {
          dispatch(setNovoAIProgressiveQuizQuestionModalId(response?.payload?.id));
        }
      });
    }
  };

  return (
    <React.Fragment>
      <GenerateButton
        onClick={() => setShowQuestionModal(true)}
        className='mt-8'
      />
      <AiModal
        show={showQuestionModal}
        onClose={() => setShowQuestionModal(false)}
        body={(
          <QuizAiQuestionsModal
            onClose={closeQuestionModal}
            onSubmit={onSelectQuestions}
          />
        )}
      />
      <NvModal
        header={t.QUIZZES.PROGRESSIVE_QUIZ_BASICS.SELECT_QUIZ_TYPE()}
        body={(
          <QuizTypeSelectionModalContent
            onAllQuestionViewSelect={() => onSubmitAllQuestionViewQuiz(questions)}
            onSingleQuestionViewSelect={() => onSubmitSingleQuestionViewQuiz(questions)}
          />
        )}
        type={ModalType.DYNAMIC}
        fullHeight={false}
        show={showQuizTypeModal}
        width={800}
        onClose={() => setShowQuizTypeModal(false)}
      />
    </React.Fragment>
  );
};

export default GenerateQuiz;

